import "../k";

const FRAME_PADDING = 68; // 34px on each side

// Get image natural dimensions
function getImageDimensions(spriteName: string, fallbackSize: number): { width: number; height: number } {
  const spriteData = getSprite(spriteName);
  if (spriteData && spriteData.data) {
    return { width: spriteData.data.tex.width, height: spriteData.data.tex.height };
  }
  return { width: fallbackSize, height: fallbackSize };
}

function calculateDimensions(naturalWidth: number, naturalHeight: number, maxDimension: number) {
  const aspectRatio = naturalWidth / naturalHeight;
  let imgWidth: number;
  let imgHeight: number;

  if (aspectRatio > 1) {
    // Landscape: keep max width, calculate height
    imgWidth = maxDimension;
    imgHeight = maxDimension / aspectRatio;
  } else {
    // Portrait or square: keep max height, calculate width
    imgHeight = maxDimension;
    imgWidth = maxDimension * aspectRatio;
  }

  return {
    imgWidth,
    imgHeight,
    frameWidth: imgWidth + FRAME_PADDING,
    frameHeight: imgHeight + FRAME_PADDING,
  };
}

function addPic(question: Question | string, isReal?: boolean, comps?: any[], maxDimension: number = 360) {
  let imageSprite: string;
  if (typeof question === "string") {
    // This case is for when a direct image path is passed (e.g., for single image question)
    imageSprite = question;
  } else if (question.type === "two-image") {
    imageSprite = isReal ? question.real : question.ai;
  } else if (question.type === "single-image") {
    imageSprite = question.image;
  } else {
    // For video/audio questions, this function shouldn't be called
    // but provide a fallback
    imageSprite = "";
  }

  const natural = getImageDimensions(imageSprite, maxDimension);
  const dims = calculateDimensions(natural.width, natural.height, maxDimension);

  const frame = add([
    sprite("frame", { width: dims.frameWidth, height: dims.frameHeight }),
    anchor("center"),
    animate(),
    area(),
    { isEnabled: true, isInspectable: true },
    ...comps,
  ]);

  const img = frame.add([
    anchor("center"),
    sprite(imageSprite, { width: dims.imgWidth, height: dims.imgHeight }),
    z(1),
    pos(),
  ]);
  frame.animate("scale", [vec2(0.8, 0.8), vec2(1, 1)], {
    duration: 0.5,
    easing: easings.easeOutElastic,
    loops: 1,
  });

  let isAnticipating = false, isHovering = false;
  let hoverStartTime = 0; 

  frame.onHoverUpdate(() => {
    if (!isHovering) { 
      hoverStartTime = time();
      isHovering = true;
    }

    if (!frame.isEnabled) {
      frame.scale = vec2(1, 1);
      frame.color = Color.WHITE;
      setCursor("default");
      isAnticipating = false; 
      return;
    }
    const t = time() * 10;
    const pulse = Math.sin(t) * 10 + 230;
    const grow = Math.sin(t) * 0.01 + 1.02;
    frame.scale = vec2(1.05);
    frame.color = rgb(pulse, pulse, pulse);
    setCursor("pointer");
    if (!isAnticipating) {
      play("rollover", { volume: 0.2 });
      isAnticipating = true;
    }
  });

  frame.onHoverEnd(() => {
    frame.scale = vec2(1, 1);
    frame.color = Color.WHITE;
    setCursor("default");
    isHovering = false;
    isAnticipating = false;
    hoverStartTime = 0; 
  });

  frame.onClick(() => {
    if (frame.isEnabled) {
      play("click", { volume: 0.5 });
    }
  });

  let targetImgWidth = dims.imgWidth;
  let targetImgHeight = dims.imgHeight;
  let targetImgPos = vec2(0);
  const LERP_SPEED = 16; // For width and height

  // Spring simulation variables for position
  let currentImgPos = vec2(0);
  let velocity = vec2(0);
  const SPRING_STIFFNESS = 250; // Adjust for more or less "springiness"
  const SPRING_DAMPING = 15; // Adjust for more or less "bounciness"

  let isZoomed; // Track zoom state for sound effects

  img.onUpdate(() => {
    const HOVER_DELAY = 0.5;
    const shouldZoom = frame.isInspectable && isHovering && (time() - hoverStartTime >= HOVER_DELAY);

    console.log(isZoomed);

    // Play zoom sounds
    if (shouldZoom && !isZoomed) {
      play("zoomIn", { volume: 0.3 });
      isZoomed = true;
    }
    if (!shouldZoom && isZoomed) {
      play("zoomOut", { volume: 0.2 });
      isZoomed = false;
    }

    const factor = 1.75;
    targetImgWidth = shouldZoom ? dims.imgWidth * factor : dims.imgWidth;
    targetImgHeight = shouldZoom ? dims.imgHeight * factor : dims.imgHeight;
    
    // Calculate target position for spring
    let newTargetImgPos = vec2(0); // Default position
    img.z = 1;

    if (shouldZoom) {
      img.z = 2;
      const delta = frame.pos.sub(mousePos()).scale(Math.log2(factor));
      newTargetImgPos = delta;
    }
    targetImgPos = newTargetImgPos; // Update the target for the spring

    // Apply lerp for smooth width and height transitions
    img.width = lerp(img.width, targetImgWidth, dt() * LERP_SPEED);
    img.height = lerp(img.height, targetImgHeight, dt() * LERP_SPEED);

    // Apply spring simulation for smooth position transition
    const displacement = targetImgPos.sub(currentImgPos);
    const acceleration = displacement.scale(SPRING_STIFFNESS).sub(velocity.scale(SPRING_DAMPING));
    velocity = velocity.add(acceleration.scale(dt()));
    currentImgPos = currentImgPos.add(velocity.scale(dt()));
    img.pos = currentImgPos;
  });

  return frame;
}

export { addPic };
